import { SplitMix64 } from "../engine/splitmix64";
import type { RunRecord } from "../engine/engine";
import type { GameStore } from "./store";
import { CATALOG } from "./catalog";

/**
 * TraceprintGenerator — deterministic route keepsake (spec §10).
 * Same RunRecord + style → same canonical layout, always. Generator version
 * is recorded so an app update never silently changes saved art (spec §10.3).
 *
 * Share-ready by design (research: Wordle-grid principles): fixed square,
 * uniform frame, glanceable, spoiler-free, footer tells a tiny story:
 * `ECHO DODGE · 27.4S · №42`.
 */

export const GENERATOR_VERSION = 1;

export interface TraceprintStyle {
  paletteId: string;
  frameId: string;
  coreColor: string;
}

export function styleFromStore(store: GameStore): TraceprintStyle {
  return {
    paletteId: store.data.equipped.palette,
    frameId: store.data.equipped.frame,
    coreColor: (store.equippedItem("core").data as { core?: string })?.core ?? "#B9F8FF",
  };
}

export function renderTraceprint(rec: RunRecord, style: TraceprintStyle, S: number): HTMLCanvasElement {
  const c = document.createElement("canvas");
  c.width = S;
  c.height = S;
  const x = c.getContext("2d")!;
  const palette = CATALOG.palette.find((p) => p.id === style.paletteId) ?? CATALOG.palette[0];
  const cols = palette.cols;

  // background
  const grad = x.createLinearGradient(0, 0, 0, S);
  grad.addColorStop(0, "#0A1022");
  grad.addColorStop(1, "#130A20");
  x.fillStyle = grad;
  x.fillRect(0, 0, S, S);

  // seed-derived grid (spec §10.2 item 6)
  const rng = new SplitMix64(BigInt(rec.seed));
  x.strokeStyle = "rgba(167,171,192,0.07)";
  x.lineWidth = Math.max(1, S / 640);
  const gn = 5 + Math.floor(rng.nextDouble() * 4);
  for (let i = 1; i < gn; i++) {
    const gp = (S * i) / gn;
    x.beginPath();
    x.moveTo(gp, 0);
    x.lineTo(gp, S);
    x.stroke();
    x.beginPath();
    x.moveTo(0, gp);
    x.lineTo(S, gp);
    x.stroke();
  }

  const h = rec.route;
  if (h.length < 2) return c;

  // Normalize the route into a centered square with 8 % padding (spec §10.2),
  // blended 40 % toward the fixed arena frame so a wall-hugging run still
  // produces a consistent, centred composition (a grid of prints must read
  // as a collection).
  let minX = Infinity,
    maxX = -Infinity,
    minY = Infinity,
    maxY = -Infinity;
  for (const p of h) {
    if (p.x < minX) minX = p.x;
    if (p.x > maxX) maxX = p.x;
    if (p.y < minY) minY = p.y;
    if (p.y > maxY) maxY = p.y;
  }
  const BLEND = 0.4;
  minX = minX * (1 - BLEND) + 20 * BLEND;
  maxX = maxX * (1 - BLEND) + 340 * BLEND;
  minY = minY * (1 - BLEND) + 48 * BLEND;
  maxY = maxY * (1 - BLEND) + 525 * BLEND;
  const w = Math.max(maxX - minX, 40);
  const hh = Math.max(maxY - minY, 40);
  const pad = S * 0.08 + S * 0.06; // 8 % spec padding + frame margin
  const sc = Math.min((S - 2 * pad) / w, (S - 2 * pad) / hh);
  const ox = (S - w * sc) / 2 - minX * sc;
  const oy = (S - hh * sc) / 2 - minY * sc;
  const M = (p: { x: number; y: number }): [number, number] => [p.x * sc + ox, p.y * sc + oy];

  // Midpoint-smoothed polyline: quadratic curves through sample midpoints so
  // routes read as drawn arcs, not surveyor's plots. Render-only smoothing —
  // the canonical route data is untouched.
  const tracePath = (from: number, to: number, stride: number, offX = 0, offY = 0): void => {
    x.beginPath();
    let prev: [number, number] | null = null;
    for (let i = from; i <= to; i += stride) {
      const pt = M(h[Math.min(i, h.length - 1)]);
      const px = pt[0] + offX;
      const py = pt[1] + offY;
      if (!prev) {
        x.moveTo(px, py);
      } else {
        x.quadraticCurveTo(prev[0], prev[1], (prev[0] + px) / 2, (prev[1] + py) / 2);
      }
      prev = [px, py];
    }
    if (prev) x.lineTo(prev[0], prev[1]);
  };

  // Four time-shifted timeline layers — the same real path differentiated by
  // weight and opacity (oldest = thinnest, faintest), with only a hairline
  // offset: ghosts of the route, not a misregistered print.
  const delays = [2.4, 4.6, 1.35, 6.2];
  const order = [3, 1, 0, 2]; // draw longest delay first (deepest layer)
  x.lineJoin = "round";
  x.lineCap = "round";
  for (const di of order) {
    const delay = delays[di];
    const skip = Math.floor(delay * 60);
    if (skip >= h.length) continue;
    const depth = delay / 6.2; // 1 = oldest
    x.strokeStyle = cols[di % cols.length];
    x.globalAlpha = 0.42 - depth * 0.28;
    x.lineWidth = S / 300 - (depth * S) / 900;
    const off = ((di + 1) * S) / 420;
    tracePath(skip, h.length - 1, 2, off, off * 0.6);
    x.stroke();
  }
  x.globalAlpha = 1;

  // player route — additive glow underlay, then the brightest line with a
  // start→end time gradient. The keepsake glows like the game it came from.
  const segs = 24;
  const per = Math.max(2, Math.floor(h.length / segs));
  x.save();
  x.globalCompositeOperation = "lighter";
  x.strokeStyle = style.coreColor;
  x.globalAlpha = 0.16;
  x.lineWidth = S / 90;
  tracePath(0, h.length - 1, 2);
  x.stroke();
  x.restore();
  x.lineWidth = S / 260;
  for (let sgi = 0; sgi * per < h.length - 1; sgi++) {
    const from = sgi * per;
    const to = Math.min(h.length - 1, from + per);
    const k = from / Math.max(1, h.length - 1);
    x.strokeStyle = style.coreColor;
    x.globalAlpha = 0.45 + 0.55 * k;
    tracePath(from, to, 2);
    x.stroke();
  }
  x.globalAlpha = 1;

  // shard pickups — soft gold nodes, size reflects combo but clamps hard so
  // dense runs stay route-forward, never polka-dot (spec §10.2 item 4)
  for (const p of rec.picks) {
    const pt = M(p);
    const r = Math.min(S / 400 + (p.combo * S) / 2400, S / 120);
    const grad = x.createRadialGradient(pt[0], pt[1], 0, pt[0], pt[1], r * 1.8);
    grad.addColorStop(0, "rgba(255,227,123,0.95)");
    grad.addColorStop(0.55, "rgba(255,227,123,0.35)");
    grad.addColorStop(1, "rgba(255,227,123,0)");
    x.fillStyle = grad;
    x.beginPath();
    x.arc(pt[0], pt[1], r * 1.8, 0, 6.283);
    x.fill();
  }

  // collisions — small broken rings (spec §10.2 item 5)
  for (const hp of rec.collisions) {
    const pt = M(hp);
    x.strokeStyle = "#FF719A";
    x.lineWidth = S / 320;
    x.beginPath();
    x.arc(pt[0], pt[1], S / 70, 0.4, 2.2);
    x.stroke();
    x.beginPath();
    x.arc(pt[0], pt[1], S / 70, 3.5, 5.4);
    x.stroke();
  }

  drawFrame(x, S, style.frameId);
  drawWordmark(x, S, rec);
  return c;
}

/**
 * Every Traceprint carries attribution — the share loop depends on the image
 * being unmistakably Echo Dodge (Wordle-grid principle). Frames may add
 * decoration on top, but no frame opts out of the wordmark.
 */
function drawWordmark(x: CanvasRenderingContext2D, S: number, rec: RunRecord): void {
  x.save();
  x.globalAlpha = 0.62;
  x.fillStyle = "#A7ABC0";
  x.font = `600 ${S / 36}px "IBM Plex Mono", monospace`;
  x.textAlign = "left";
  x.fillText("ECHO DODGE", S * 0.06, S * 0.955);
  x.textAlign = "right";
  const score = rec.kind === "practice" ? "" : `${rec.score.toLocaleString()} · `;
  x.fillText(
    `${score}${rec.duration.toFixed(1)}S · ${rec.dateISO.slice(0, 10)}`,
    S * 0.94,
    S * 0.955,
  );
  x.restore();
}

function drawFrame(x: CanvasRenderingContext2D, S: number, frameId: string): void {
  const inset = S / 128;
  switch (frameId) {
    case "meridian":
      x.strokeStyle = "rgba(66,218,246,0.5)";
      x.lineWidth = S / 240;
      x.strokeRect(inset * 2, inset * 2, S - inset * 4, S - inset * 4);
      break;
    case "aurora_f": {
      const fg = x.createLinearGradient(0, 0, S, S);
      fg.addColorStop(0, "#42DAF6");
      fg.addColorStop(0.5, "#9F79FF");
      fg.addColorStop(1, "#FF61D2");
      x.strokeStyle = fg;
      x.lineWidth = S / 64;
      x.strokeRect(inset, inset, S - inset * 2, S - inset * 2);
      break;
    }
    case "archive":
      x.strokeStyle = "rgba(167,171,192,0.7)";
      x.lineWidth = S / 200;
      x.strokeRect(inset * 2, inset * 2, S - inset * 4, S - inset * 4);
      x.strokeRect(inset * 3.5, inset * 3.5, S - inset * 7, S - inset * 7);
      break;
    case "orbital":
      x.strokeStyle = "rgba(255,227,123,.5)";
      x.lineWidth = S / 320;
      x.beginPath();
      x.arc(S / 2, S / 2, S / 2 - S / 24, 0, 6.283);
      x.stroke();
      break;
    case "spectrum": {
      const fg = x.createLinearGradient(0, S, S, 0);
      fg.addColorStop(0, "#FF61D2");
      fg.addColorStop(0.5, "#9F79FF");
      fg.addColorStop(1, "#42DAF6");
      x.strokeStyle = fg;
      x.lineWidth = S / 100;
      x.beginPath();
      x.arc(S / 2, S / 2, S / 2 - S / 28, 0, 6.283);
      x.stroke();
      break;
    }
    case "gilded":
      x.strokeStyle = "#FFE37B";
      x.lineWidth = S / 90;
      x.strokeRect(inset, inset, S - inset * 2, S - inset * 2);
      x.strokeStyle = "rgba(255,243,196,0.6)";
      x.lineWidth = S / 400;
      x.strokeRect(inset * 4, inset * 4, S - inset * 8, S - inset * 8);
      break;
    case "nebula": {
      const fg = x.createRadialGradient(S / 2, S / 2, S / 4, S / 2, S / 2, S / 2);
      fg.addColorStop(0, "rgba(159,121,255,0)");
      fg.addColorStop(1, "rgba(255,97,210,0.28)");
      x.fillStyle = fg;
      x.fillRect(0, 0, S, S);
      break;
    }
    default:
      return; // plain — decoration-free; the wordmark still draws separately
  }
}

export function traceprintDataURL(rec: RunRecord, style: TraceprintStyle, size = 640): string {
  return renderTraceprint(rec, style, size).toDataURL("image/jpeg", 0.88);
}
